import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Button from './Button';

interface DatePickerProps {
  placeholder?: string;
  showValueInField?: boolean; // if true, show selected range in the input
  textFieldSx?: SxProps<Theme>;
  onApply?: (_range: { startDate: Date; endDate: Date | null }) => void;
  iconSx?: SxProps<Theme>;
  value?: { startDate: Date; endDate: Date | null } | null | undefined;
}

const DatePicker: React.FC<DatePickerProps> = ({
  placeholder: placeholderProp,
  showValueInField = false,
  textFieldSx,
  onApply,
  iconSx,
  value,
}) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp ?? t('common.selectDate');
  // committed value
  const [ranges, setRanges] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' as const },
  ]);
  // draft value while popover is open
  const [tempRanges, setTempRanges] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' as const },
  ]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    // initialize draft from committed when opening
    setTempRanges(ranges);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    setRanges(tempRanges);
    onApply?.({
      startDate: tempRanges[0].startDate as Date,
      endDate: tempRanges[0].endDate as Date | null,
    });
    handleClose();
  };

  const handleCancel = () => {
    setTempRanges(ranges);
    handleClose();
  };

  const showValueInInputField = showValueInField
    ? (() => {
        const start = ranges?.[0]?.startDate as Date | undefined;
        const end = (ranges?.[0]?.endDate as Date | null) || null;

        const fmt = (d: Date) => {
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yyyy = d.getFullYear();
          return `${yyyy}/${mm}/${dd}`;
        };

        if (!start) return '';
        if (!end) return fmt(start);

        const sameDay =
          start.getFullYear() === end.getFullYear() &&
          start.getMonth() === end.getMonth() &&
          start.getDate() === end.getDate();

        if (sameDay) return fmt(start);
        return `${fmt(start)} - ${fmt(end)}`;
      })()
    : '';

  // Sync external value into internal state
  useEffect(() => {
    if (value && value.startDate) {
      const start = value.startDate instanceof Date ? value.startDate : new Date(value.startDate);
      const end = value.endDate
        ? value.endDate instanceof Date
          ? value.endDate
          : new Date(value.endDate as any)
        : start;
      const next = [{ startDate: start, endDate: end, key: 'selection' as const }];
      setRanges(next);
      setTempRanges(next);
    }
  }, [value?.startDate, value?.endDate, value]);

  return (
    <>
      <TextField
        value={showValueInInputField}
        onClick={handleOpen}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: 'none',
            },
          },
          '& .MuiOutlinedInput-input': {
            padding: '3px 8px',
            fontSize: '12px',
            cursor: 'pointer',
          },
          ...(textFieldSx as any),
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpen} edge="end" sx={{ p: 0, ...iconSx }}>
                <CalendarMonthIcon sx={{ fontSize: '15px' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        fullWidth
        placeholder={placeholder}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          <DateRange
            className="dateRangeSmall"
            editableDateInputs
            onChange={(item: any) => {
              setTempRanges([item.selection]);
            }}
            moveRangeOnFirstSelection={false}
            ranges={tempRanges}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: '10px', padding: '2px 5px', minWidth: '70px', height: '30px' }}
              onClick={handleCancel}
              color="inherit"
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              size="small"
              variant="blue"
              sx={{ fontSize: '10px', padding: '2px 5px', minWidth: '70px', height: '30px' }}
              onClick={handleApply}
            >
              {t('common.apply')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DatePicker;
